import {Domain, HandleReturnType} from './domain.js';
import {failure, globalDomainUri} from './constants.js';
import {getLogger} from './logger.js';
import {error} from './common/messages.js';
import {reason} from './reason.js';
import {byId} from './state/peers.js';
import {State} from './state/types/state.js';
import {DomainUri, RegisteredDomains} from './state/types/domain-registry.js';
import {PeerId, Source, OriginType} from './state/types/common.js';
import {exceptionInfo} from './common/types.js';
import {PeerMessage} from './common/types/messages.js';
import {IOGateway} from '../gateway';

export type CommandType = 'commands/source-removed';
export type DomainType = 'domain/join' | 'domain/leave';

export type InternalType
    = 'internal/authenticated'
    | 'internal/authentication-request'
    | 'internal/authentication-failed';

export type ContextRequestType
    = 'create-context'
    | 'update-context'
    | 'subscribe-context'
    | 'unsubscribe-context'
    | 'destroy-context';

export type GlobalRequestType
    = 'hello'
    | 'join'
    | 'leave'
    | 'ping'
    | 'create-token'
    | ContextRequestType;

export type GlobalResponseType
    = 'welcome'
    | 'authentication-request'
    | 'token'
    | ContextResponseType;

export type ContextResponseType = 'subscribed-context' | 'context-created';
export type ContextBroadcastType = 'context-added' | 'context-destroyed' | 'context-updated';
export type ActivityIncomingType
    = 'ready'
    | 'subscribe'
    | 'unsubscribe'
    | 'add-types'
    | 'remove-types'
    | 'create'
    | 'destroy'
    | 'join-activity'
    | 'leave-activity'
    | 'add-peer-factories'
    | 'remove-peer-factories'
    | 'create-peer'
    | 'destroy-peer'
    | 'reload'
    | 'error'
    | 'update-context'
    ;
export type ActivityResponseType = 'accepted' | 'initiated' | 'peer-requested';
export type ActivityBroadcastType
    = 'peer-added'
    | 'peer-removed'
    | 'peer-factories-added'
    | 'peer-factories-removed'
    | 'types-removed'
    | 'types-added'
    | 'peer-created'
    | 'dispose-peer'
    | 'activity-joined'
    | 'activity-left'
    | 'left'
    | 'joined'
    | 'created'
    | 'destroyed'
    | 'owner-changed';

export type MetricsIncomingType = 'publish' | 'define';
export type BusIncomingType = 'publish' | 'subscribe' | 'unsubscribe';

export type AgmIncomingType
    = 'register'
    | 'unregister'
    | 'call'
    | 'yield'
    | 'subscribe'
    | 'unsubscribe'
    | 'accepted'
    | 'drop-subscription'
    | 'publish'
    | 'post'
    | 'error'
    ;

export type AgmResponseType
    = 'subscribed'
    | 'result';

export type AgmBroadcastType
    = 'peer-added'
    | 'methods-added'
    | 'methods-removed'
    | 'add-interest'
    | 'remove-interest'
    | 'invoke'
    | 'subscription-cancelled'
    | 'event';

export type RequestType = GlobalRequestType | AgmIncomingType | ContextRequestType | ActivityIncomingType | BusIncomingType | MetricsIncomingType;
export type ResponseType = GlobalResponseType | AgmResponseType | ContextResponseType | ActivityResponseType;
export type BroadcastType = AgmBroadcastType | ContextBroadcastType | ActivityBroadcastType;

export type MessageType = RequestType | ResponseType | BroadcastType | 'error' | 'success' | 'token';

export interface DomainMessage extends IOGateway.Message<MessageType> {
    domain: DomainUri
}

// export interface BroadcastMessage<T extends BroadcastType> extends Message<T>, DomainMessage, PeerMessage {}

export type SendMessageAction<T> = { receiver: Source, body: IOGateway.Message<T> };


export type NodeMessage<T> = { origin: OriginType, source: Source, body: IOGateway.Message<T> }

export type NodeOptions = { bufferSize?: number, nodeId?: string, signingKey?: string };

export interface GatewayNode {
    message(msg: NodeMessage<RequestType>): void;

    addSource(source: Source): void;

    removeSource(source: Source): void;

    close(): void;
}

function matchSource(source1: Source, source2: Source): boolean {
    if (source1 === source2) {
        return true;
    }
    const sourceKeys = Object.keys(source1);
    if (sourceKeys.length !== Object.keys(source2).length) {
        return false;
    }
    for (const key of sourceKeys) {
        // only level one nesting is enough.
        if (source1[key] !== source2[key]) {
            return false;
        }
    }
    return true;
}

function validateSource(state: State, source: Source, body: Partial<PeerMessage>): void {
    const {peer_id: peerId} = body;
    const peer = byId(state, peerId);
    if (peer && !matchSource(source, peer.source)) {
        throw exceptionInfo(`The original source ${JSON.stringify(peer.source)} of peer ${peerId} doesnt match the current source ${JSON.stringify(source)}`, {message: 'Bad Source'});
    }
}

const log = getLogger('gateway.node');

/*
 * sends source-removed command to all domains while keeping the global one last
 */
function sourceRemoved(msg: NodeMessage<'commands/source-removed'>, state: State): HandleReturnType {

    const domains = Object.values(state.registeredDomains!)
        .filter((registeredDomain) => {
            return registeredDomain.info.uri !== globalDomainUri
        }).map(registeredDomain => registeredDomain.domain);
    if (state.registeredDomains![globalDomainUri]) {
        domains.push(state.registeredDomains![globalDomainUri].domain);
    }

    return domains.reduce(([curState, curMsgs], domain) => {
        if (log.enabledFor('debug')) {
            log.debug(`About to remove source from domain ${JSON.stringify(domain.info())}`);
        }
        const result = domain.handleMessage(curState, msg);
        if (result) {
            if (log.enabledFor('debug')) {
                log.debug(`removed source from domain ${JSON.stringify(domain.info())}`);
            }
            const [newState, newMsgs] = result;
            return [newState, curMsgs.concat(newMsgs)];
        } else {
            return [curState, curMsgs];
        }
    }, [state, []] as HandleReturnType);
}

export function handleMessage(state: State, msg: NodeMessage<CommandType | InternalType | RequestType>, source: Source): HandleReturnType {
    if (msg.body.type === 'commands/source-removed') {
        return sourceRemoved(msg as NodeMessage<CommandType>, state);
    } else {
        const {body} = msg;
        const {registeredDomains: domains} = state;
        const domainUri: DomainUri = (body as DomainMessage).domain ?? globalDomainUri;
        const domain = domains![domainUri]?.domain;
        if (domain) {
            if (log.enabledFor('debug')) {
                log.debug(`Handling message with domain ${JSON.stringify(domain.info())} message: \n ${JSON.stringify(msg, null, '\t')}`);
            }
            validateSource(state, source, body as { peer_id?: PeerId });
            return domain.handleMessage(state, msg);
        } else {
            const m = body as { domain?: DomainUri, request_id?, peer_id?: PeerId };
            return [state, [error(m.domain, source, m.request_id, m.peer_id, reason(failure, `Unable to find domain for message ${JSON.stringify(msg)}`))]]
        }
    }
}

export function buildDomains<T extends MessageType = MessageType>(domains: Domain<T>[]): RegisteredDomains {
    return domains.reduce((target, domain) => {
        const info = domain.info();
        const source: RegisteredDomains = {};
        source[info.uri] = {domain, info};
        return Object.assign(target, source);
    }, {});
}
